import { FormInput, Popover, PrimaryButton } from '@systemeio/ui'
import { addField } from 'modules/contacts/contact-page/api/contact-edit-api'
import { ContactFieldCreateInterface } from 'modules/contacts/contact-page/types/contact-edit-interface'
import { ContactFieldApiInterface } from 'modules/contacts/types/contact-filter-interface'
import React, { useState } from 'react'
import CreateModal from 'shared/components/create-modal'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { KeyedMutator } from 'swr'
import { AddIcon, QuestionMarkIcon } from '@systemeio/ui/icons'

interface ContactAddFieldProps {
  mutateFields: KeyedMutator<ContactFieldApiInterface>
  disabled: boolean
}

const defaultValues: ContactFieldCreateInterface = {
  fieldName: '',
  slug: '',
}

const defaultErrors: Record<keyof ContactFieldCreateInterface, string> = {
  fieldName: '',
  slug: '',
}

const ContactAddField = ({ mutateFields, disabled }: ContactAddFieldProps) => {
  const { t } = useLocoTranslation()
  const [opened, setOpened] = useState(false)
  const [errors, setErrors] =
    useState<Record<keyof ContactFieldCreateInterface, string>>(defaultErrors)

  const onCreate = async (data: ContactFieldCreateInterface) => {
    await fetchWithErrors(async () => {
      const response = await addField(data)
      await mutateFields(data => {
        if (data) {
          return { ...data, items: [...data.items, response.data] }
        }
      }, false)
    }, setErrors)
  }

  return (
    <div className={'flex h-[42px] justify-center lg:pl-5'}>
      <PrimaryButton disabled={disabled} onClick={() => setOpened(true)} className={'w-fit'}>
        <AddIcon className={'main-transition-colors'} />
        <span>{t('dashboard.contact_page.add_new_field')}</span>
      </PrimaryButton>
      <CreateModal
        caption={t('dashboard.contact_page.custom_field')}
        opened={opened}
        onClose={() => setOpened(false)}
        defaultValues={defaultValues}
        creatable={[
          {
            field: 'fieldName',
            onRender: (value, onChange, state, onChangeState) => (
              <FormInput
                key={'fieldName-create'}
                label={t('global.name')}
                value={value}
                onChange={e => {
                  onChangeState({
                    ...state,
                    fieldName: e.target.value,
                    slug: e.target.value
                      .replaceAll(' ', '_')
                      .replace(/[^a-zA-Z|\-_\d+]/g, '')
                      .toLowerCase(),
                  })
                }}
                error={errors.fieldName}
              />
            ),
          },
          {
            field: 'slug',
            onRender: (value, onChange) => (
              <FormInput
                key={'slug-create'}
                label={
                  <div className={'flex items-center gap-1.5'}>
                    {t('dashboard.contact_page.unique_key')}
                    <Popover
                      label={t('dashboard.contact_page.unique_key_description')}
                      popoverClassName={'w-[300px] sm:w-[350px]'}
                    >
                      {show => (
                        <QuestionMarkIcon
                          className={`${
                            show ? 'fill-brand-primary' : 'fill-fill-primary'
                          } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                        />
                      )}
                    </Popover>
                  </div>
                }
                value={value}
                onChange={e => onChange(e.target.value)}
                error={errors.slug}
              />
            ),
          },
        ]}
        onCreate={onCreate}
        requiredFields={['fieldName', 'slug']}
      />
    </div>
  )
}

export default ContactAddField
